<template>
  <!-- Modern Checkout Section -->
  <section class="modern-checkout">
    <div class="container">
      <!-- Loader Overlay -->
      <div class="loader-overlay" v-if="loader">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">{{ $t("sentence.loadingMore") }}</span>
        </div>
      </div>

      <!-- Error Alert -->
      <transition name="fade">
        <div class="row mb-4" v-if="has_error && !success">
          <div class="col-12">
            <div class="alert-modern alert-danger">
              <i class="icon-alert">⚠️</i>
              <span>{{ error }}</span>
            </div>
          </div>
        </div>
      </transition>

      <form class="container-nzidoo card mx-2 p-3">
        <div class="row">
          <!-- Left Column: Billing Details -->
          <div class="col-lg-7 mb-4">
            <div class="checkout-card">
              <!-- Saved Addresses -->
              <div class="saved-addresses" v-if="orderInformation.length > 0">
                <h5 class="section-title">
                  <i class="icon">📍</i>
                  {{ $t("checkout.chooseOrderInfo") }}
                </h5>
                <div class="address-grid">
                  <div v-for="information in orderInformation" :key="information.id" class="address-card"
                    :class="{ 'selected': selectedOrderInformation.id === information.id }"
                    @click="checkOrderInformation(information.id)">
                    <input type="radio" :value="information.id"
                      :checked="selectedOrderInformation.id === information.id" class="address-radio" />
                    <div class="address-content">
                      <h6>{{ information.delivery_firstname }} {{ information.delivery_lastname }}</h6>
                      <p>{{ information.delivery_street }}</p>
                      <span class="badge-location">{{ information.delivery_city }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Login Prompt -->
              <div v-if="user.id == undefined" class="login-prompt">
                <div class="prompt-icon">🔐</div>
                <p>{{ $t("sentence.clickHereToLogin") }}</p>
                <router-link :to="{ name: 'login' }" class="btn-login">
                  Login to Continue
                </router-link>
              </div>

              <!-- Billing Form -->
              <div class="billing-section">
                <h5 class="section-title">
                  <i class="icon">📋</i>
                  {{ $t("ecommerce.billing-details") }}
                </h5>

                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="form-group-modern">
                      <label>{{ $t("attr.firstname") }}<span class="required">*</span></label>
                      <input type="text" class="form-control-modern"
                        :class="{ 'is-invalid': has_error && errors.delivery_firstname }"
                        v-model="selectedOrderInformation.delivery_firstname" placeholder="John" />
                      <span class="error-text" v-if="has_error && errors.delivery_firstname">
                        {{ errors.delivery_firstname[0] }}
                      </span>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="form-group-modern">
                      <label>{{ $t("attr.lastname") }}<span class="required">*</span></label>
                      <input type="text" class="form-control-modern"
                        :class="{ 'is-invalid': has_error && errors.delivery_lastname }"
                        v-model="selectedOrderInformation.delivery_lastname" placeholder="Doe" />
                      <span class="error-text" v-if="has_error && errors.delivery_lastname">
                        {{ errors.delivery_lastname[0] }}
                      </span>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="form-group-modern">
                      <label>{{ $t("attr.address") }}<span class="required">*</span></label>
                      <input type="text" class="form-control-modern"
                        :class="{ 'is-invalid': has_error && errors.delivery_street }"
                        v-model="selectedOrderInformation.delivery_street" placeholder="123 Main Street" />
                      <span class="error-text" v-if="has_error && errors.delivery_street">
                        {{ errors.delivery_street[0] }}
                      </span>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="form-group-modern">
                      <label>{{ $t("attr.postal_code") }} ({{ $t("attr.optional") }})</label>
                      <input type="text" class="form-control-modern"
                        :class="{ 'is-invalid': has_error && errors.delivery_postal_code }"
                        v-model="selectedOrderInformation.delivery_postal_code" placeholder="12345" />
                      <span class="error-text" v-if="has_error && errors.delivery_postal_code">
                        {{ errors.delivery_postal_code[0] }}
                      </span>
                    </div>
                  </div>

                  <AddressSelector :errors="errors" :has_error="has_error" />
                  <div class="col-md-6">
                    <div class="form-group-modern">
                      <label>{{ $t("attr.phone") }}<span class="required">*</span></label>
                      <input type="text" class="form-control-modern"
                        :class="{ 'is-invalid': has_error && errors.delivery_phone }"
                        v-model="selectedOrderInformation.delivery_phone" placeholder="+213 555 1234" />
                      <span class="error-text" v-if="has_error && errors.delivery_phone">
                        {{ errors.delivery_phone[0] }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: Order Summary -->
          <div class="col-lg-5">
            <div class="order-summary-card sticky-top">
              <h5 class="section-title">
                <i class="icon">🛒</i>
                {{ $t("general.your") }} {{ $t("ecommerce.order") }}
              </h5>

              <!-- Single Product Item -->
              <div class="product-summary" v-if="type !== 'batch' && bid != undefined && bid.product != undefined">
                <div class="product-item">
                  <img v-if="Array.isArray(bid.product.pictures)" class="product-thumb"
                    :src="config.storageUrl + bid.product.pictures[0]" alt="Product" />
                  <img v-else class="product-thumb" :src="config.storageUrl + bid.product.pictures" alt="Product" />
                  <div class="product-info">
                    <h6>{{ bid.product.name }}</h6>
                    <p class="quantity">{{ $t("attr.quantity") }}: {{ bid.quantity }}</p>
                  </div>
                  <div class="product-price">
                    {{ (parseFloat(bid.quantity) * parseFloat(bid.price)) | currency(" DA", 0, { symbolOnLeft: false })
                    }}
                  </div>
                </div>
              </div>

              <!-- Batch Products List -->
              <div class="product-summary" v-if="type === 'batch' && batchProducts.length > 0">
                <div v-for="item in batchProducts" :key="item.product.id" class="product-item mb-3">
                  <img v-if="Array.isArray(item.product.pictures)" class="product-thumb"
                    :src="config.storageUrl + item.product.pictures[0]" alt="Product" />
                  <img v-else class="product-thumb" :src="config.storageUrl + item.product.pictures" alt="Product" />
                  <div class="product-info">
                    <h6>{{ item.product.name }}</h6>
                    <p class="quantity">{{ $t("attr.quantity") }}: {{ item.quantity }}</p>
                  </div>
                  <div class="product-price">
                    {{ (parseFloat(item.quantity) * parseFloat(item.price)) | currency(" DA", 0, {
                      symbolOnLeft: false
                    }) }}
                  </div>
                </div>
              </div>

              <!-- Price Breakdown -->
              <div class="price-breakdown">
                <div class="price-row">
                  <span>{{ $t("checkout.shippingCost") }}</span>
                  <span class="price-value">{{ (parseFloat(800)) | currency(" DA", 0, { symbolOnLeft: false }) }}</span>
                </div>
                <div class="divider"></div>
                <div class="price-row total-row">
                  <span>{{ $t("ecommerce.total") }}</span>
                  <span class="total-price"
                    v-if="(type !== 'batch' && bid.product != undefined) || (type === 'batch' && batchProducts.length > 0)">
                    {{ (parseFloat(totalPrice) + 800) | currency(" DA", 0, { symbolOnLeft: false }) }}
                  </span>
                </div>
              </div>

              <!-- Payment Methods -->
              <div class="payment-check">
                <div v-for="paymentType in paymentTypes" :key="paymentType.id" class="pc-item">
                  <label class="d-flex justify-between align-items-center w-100">
                    <input type="radio" :name="'payment_radio'" class="payment-radio" :id="paymentType.code"
                      :value="paymentType.id" v-model="payment" />
                    <img :src="`${config.baseurl}${paymentType.icon}`" :alt="paymentType.name" class="payment-icon" />
                    {{ paymentType.name }}
                  </label>
                </div>
              </div>

              <!-- Place Order Button -->
              <button type="button" class="btn btn-site" @click="placeOrder">
                <span>{{ $t("action.placeOrder") }}</span>
                <i class="arrow">→</i>
              </button>
            </div>
          </div>
        </div>
      </form>

      <notifications style="top:5%;" width="35%" group="success" position="top center" />
      <notifications style="top:5%;" width="35%" group="error" position="top center" />
    </div>
  </section>
</template>

<script>
import AddressSelector from "@/components/templates/AddressSelector.vue";
import config from "@/config";

export default {
  beforeMount() {
    this.getPaymentType();
    if (this.user.id!==null) {
      this.checkoutInformation();
      if (this.user&&this.user.id!==undefined) {
        this.selectedOrderInformation={
          delivery_firstname: this.user.firstname||"",
          delivery_lastname: this.user.lastname||"",
          delivery_street: this.user.address||"",
          delivery_city: this.user.city||"",
          delivery_country: this.user.country||"",
          delivery_phone: this.user.phone||"",
          delivery_postal_code: this.user.postal_code||"",
          wilaya_id: this.user.wilaya_id||null,
          commune_id: this.user.commune_id||null,
        };
      }
    }
    if (this.$route.params.id!=undefined) {
      if (this.$route.params.type=="bid") {
        this.getBids(this.$route.params.id);
        this.type="bid";
      }
      if (this.$route.params.type=="offer") {
        this.getOffers(this.$route.params.id);
        this.type="offer";
      }
      if (this.$route.params.type=="product") {
        this.getProduct(this.$route.params.id);
        this.type="product";
      }
      if (this.$route.params.type=="batch") {
        const productIds=this.$route.params.id.split(',').map(id => parseInt(id.trim()));
        this.getBatchProducts(productIds);
        this.type="batch";
      }
    }
  },
  data() {
    return {
      orderInformation: [],
      selectedOrderInformation: [],
      bid: [],
      offer: [],
      batchProducts: [], // For batch type
      totalPrice: 0,
      paymentTypes: [],
      payment: null,
      checkedOrderId: [],
      type: "direct",
      loader: false,
      config: config,
      has_error: false,
      error: "",
      errors: {},
    };
  },
  components: {
    AddressSelector,
  },
  methods: {
    getBids(id) {
      var self=this;
      this.$axios({
        url: "bids/list/"+id,
        method: "GET",
      })
        .then((response) => {
          self.bid=response.data[0];
          self.totalPrice=
            parseFloat(self.bid.quantity)*parseFloat(self.bid.price);
        })
        .catch(function (err) {
          console.log(err);
        });
    },
    getOffers(id) {
      var self=this;
      this.$store;
      this.$axios({
        url: "offers/list/"+id,
        method: "GET",
      })
        .then((response) => {
          const offerData=response.data[0];
          // Get the first product from offer_products
          const firstOfferProduct=offerData.offer_products&&offerData.offer_products[0];

          // Map offer data to bid structure expected by template
          self.bid={
            quantity: firstOfferProduct? firstOfferProduct.quantity:(offerData.quantity||1),
            price: offerData.price,
            product: firstOfferProduct? firstOfferProduct.product:null
          };

          // Calculate total price
          self.totalPrice=parseFloat(self.bid.quantity)*parseFloat(self.bid.price);
        })
        .catch(function (err) {
          console.log(err);
          self.$notify({
            group: "error",
            title: "Erreur !",
            type: "error",
            text: "Erreur lors du chargement de l'offre.",
          });
        });
    },
    getProduct(id) {
      var self=this;
      this.$axios({
        url: "product/get/"+id,
        method: "GET",
      })
        .then((response) => {
          const productData=response.data;
          const quantity=self.$route.params.quantity||1;
          // Map product data to bid structure expected by template
          self.bid={
            product: productData,
            price: productData.fixed_price||productData.price,
            quantity: quantity
          };
          self.totalPrice=parseFloat(self.bid.quantity)*parseFloat(self.bid.price);
        })
        .catch(function (err) {
          console.log(err);
        });
    },
    getBatchProducts(productIds) {
      var self=this;
      // Fetch multiple products by IDs
      const promises=productIds.map(id =>
        this.$axios({
          url: "product/get/"+id,
          method: "GET",
        })
      );

      Promise.all(promises)
        .then((responses) => {
          // Map each product to include quantity and price
          self.batchProducts=responses.map((response) => {
            const productData=response.data;
            return {
              product: productData,
              price: productData.fixed_price||productData.price,
              quantity: 1 // Default quantity for batch items
            };
          });

          // Calculate total price for all batch products
          self.totalPrice=self.batchProducts.reduce((sum, item) => {
            return sum+(parseFloat(item.quantity)*parseFloat(item.price));
          }, 0);
        })
        .catch(function (err) {
          console.log(err);
          self.$notify({
            group: "error",
            title: "Erreur !",
            type: "error",
            text: "Erreur lors du chargement des produits.",
          });
        });
    },
    checkoutInformation() {
      var self=this;
      this.$store;
      this.$axios({
        url: "orderInformation/list",
        method: "GET",
      })
        .then((response) => {
          self.orderInformation=response.data;
        })
        .catch(function (err) {
          console.log(err);
        });
    },
    getPaymentType() {
      var self=this;
      this.$axios({
        // url: "paymentType/list",
        url: "payment-methods",
        method: "GET",
      })
        .then((response) => {
          self.paymentTypes=Object.values(response.data.data);
        })
        .catch(function (err) {
          console.log(err);
        });
    },
    placeOrder() {
      var self=this;
      var order_information_id=-1;
      var id=-1;
      self.loader=true;
      id=this.$route.params.id;
      var data={};

      // For batch type, pass products_id as array
      if (self.type==='batch') {
        const productIds=id.split(',').map(id => parseInt(id.trim()));
        if (self.selectedOrderInformation.id!=null) {
          data={
            paiement_types_id: self.payment,
            order_information_id: self.selectedOrderInformation.id,
            products_id: productIds,
            type: self.type,
          };
        } else {
          data={
            paiement_types_id: self.payment,
            order_information_id: order_information_id,
            delivery_firstname: self.selectedOrderInformation.delivery_firstname,
            delivery_lastname: self.selectedOrderInformation.delivery_lastname,
            delivery_street: self.selectedOrderInformation.delivery_street,
            delivery_city: self.selectedOrderInformation.delivery_city,
            delivery_country: self.selectedOrderInformation.delivery_country,
            delivery_phone: self.selectedOrderInformation.delivery_phone,
            delivery_postal_code:
              self.selectedOrderInformation.delivery_postal_code,
            wilaya_id: self.selectedOrderInformation.wilaya_id,
            commune_id: self.selectedOrderInformation.commune_id,
            products_id: productIds,
            type: self.type,
          };
        }
      } else {
        // For other types (bid, offer, product)
        if (self.selectedOrderInformation.id!=null) {
          data={
            paiement_types_id: self.payment,
            order_information_id: self.selectedOrderInformation.id,
            id: id,
            type: self.type,
          };
        } else {
          data={
            paiement_types_id: self.payment,
            order_information_id: order_information_id,
            delivery_firstname: self.selectedOrderInformation.delivery_firstname,
            delivery_lastname: self.selectedOrderInformation.delivery_lastname,
            delivery_street: self.selectedOrderInformation.delivery_street,
            delivery_city: self.selectedOrderInformation.delivery_city,
            delivery_country: self.selectedOrderInformation.delivery_country,
            delivery_phone: self.selectedOrderInformation.delivery_phone,
            delivery_postal_code:
              self.selectedOrderInformation.delivery_postal_code,
            wilaya_id: self.selectedOrderInformation.wilaya_id,
            commune_id: self.selectedOrderInformation.commune_id,
            id: id,
            type: self.type,
          };
        }
      }
      this.$axios({
        url: "orders/create",
        method: "POST",
        data: data,
      })
        .then(() => {
          self.loader=false;
          self.$notify({
            group: "success",
            title: "Bravo !",
            type: "success",
            text: "Votre action a été enregistrée avec succès!",
          });
          self.$router.push({ name: "myOperations", params: { activeTab: 'purchases' } });
        })
        .catch(function (err) {
          self.$notify({
            group: "error",
            title: "Erreur !",
            type: "error",
            text: self.$t(err.response.data.message),
          });
          self.loader=false;
          self.has_error=true;
          self.error=self.$t(err.response.data.message);
          self.errors=err.response.data.data||{};
          self.loader=false;
        });
    },
    checkOrderInformation(orderInformationId) {
      var self=this;
      self.selectedOrderInformation=self.orderInformation.filter(
        (i) => i.id==orderInformationId
      )[0];
    },
  },
  computed: {
    user: {
      get() {
        return this.$store.state.user;
      },
      set(user) {
        this.$store.commit("setUser", { user: user });
      },
    },
  },
};
</script>

<style scoped>
/* Modern Checkout Styles */
.modern-checkout {
  padding: 60px 0;
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--white) 100%);
  min-height: 100vh;
}

/* Loader Overlay */
.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
}

/* Alert Modern */
.alert-modern {
  padding: 16px 20px;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fee;
  border: 1px solid var(--color-oo4);
  color: var(--color-oo3);
  box-shadow: var(--shadow);
  animation: slideDown 0.3s ease;
}

.icon-alert {
  font-size: 24px;
}

/* Cards */
.checkout-card,
.order-summary-card {
  background: var(--white);
  border-radius: var(--border-radius);
  padding: 32px;
  box-shadow: var(--shadow);
  border: 1px solid var(--gray-200);
}

.order-summary-card {
  position: sticky;
  top: 20px;
}

/* Section Titles */
.section-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-dark);
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.icon {
  font-size: 24px;
}

/* Saved Addresses */
.saved-addresses {
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 2px solid var(--gray-100);
}

.address-grid {
  display: grid;
  gap: 16px;
}

.address-card {
  padding: 16px;
  border: 2px solid var(--gray-200);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  gap: 12px;
  background: var(--gray-50);
}

.address-card:hover {
  border-color: var(--color-nz);
  background: var(--white);
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}

.address-card.selected {
  border-color: var(--color-nz);
  background: rgba(38, 149, 255, 0.05);
  box-shadow: 0 0 0 3px rgba(38, 149, 255, 0.1);
}

.address-radio {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: var(--color-nz);
}

.address-content h6 {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
  margin-bottom: 4px;
}

.address-content p {
  font-size: 14px;
  color: var(--gray-600);
  margin-bottom: 8px;
}

.badge-location {
  display: inline-block;
  padding: 4px 12px;
  background: var(--color-nz);
  color: var(--white);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

/* Login Prompt */
.login-prompt {
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, rgba(38, 149, 255, 0.1), rgba(38, 149, 255, 0.05));
  border-radius: var(--border-radius);
  margin-bottom: 32px;
}

.prompt-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.login-prompt p {
  color: var(--gray-700);
  margin-bottom: 20px;
  font-size: 16px;
}

.btn-login {
  display: inline-block;
  padding: 12px 32px;
  background: var(--color-nz);
  color: var(--white);
  border-radius: var(--border-radius-sm);
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: var(--shadow);
}

.btn-login:hover {
  background: var(--color-nz2);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  text-decoration: none;
  color: var(--white);
}

/* Form Groups */
.form-group-modern {
  margin-bottom: 0;
}

.form-group-modern label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-dark);
  margin-bottom: 8px;
}

.required {
  color: var(--color-oo);
  margin-left: 4px;
}

.form-control-modern {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--gray-200);
  border-radius: var(--border-radius-sm);
  font-size: 15px;
  transition: all 0.3s ease;
  background: var(--white);
}

.form-control-modern:focus {
  outline: none;
  border-color: var(--color-nz);
  box-shadow: 0 0 0 3px rgba(38, 149, 255, 0.1);
}

.form-control-modern.is-invalid {
  border-color: var(--color-oo);
}

.error-text {
  display: block;
  color: var(--color-oo);
  font-size: 13px;
  margin-top: 6px;
}

/* Product Summary */
.product-summary {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid var(--gray-100);
}

.product-item {
  display: flex;
  gap: 16px;
  align-items: center;
}

.product-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--gray-200);
  flex-shrink: 0;
}

.product-info {
  flex: 1;
}

.product-info h6 {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
  margin-bottom: 4px;
}

.product-info .quantity {
  font-size: 14px;
  color: var(--gray-600);
  margin: 0;
}

.product-price {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-nz);
}

/* Price Breakdown */
.price-breakdown {
  margin-bottom: 24px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  font-size: 15px;
  color: var(--gray-700);
}

.price-value {
  font-weight: 600;
  color: var(--color-dark);
}

.divider {
  height: 1px;
  background: var(--gray-200);
  margin: 8px 0;
}

.total-row {
  padding: 16px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-dark);
}

.total-price {
  color: var(--color-nz);
  font-size: 24px;
}

/* Payment Methods */
.payment-methods {
  margin-bottom: 24px;
}

.payment-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
  margin-bottom: 16px;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-option {
  padding: 16px;
  border: 2px solid var(--gray-200);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--gray-50);
}

.payment-option:hover {
  border-color: var(--color-nz);
  background: var(--white);
  transform: translateX(4px);
}

.payment-option.selected {
  border-color: var(--color-nz);
  background: rgba(38, 149, 255, 0.05);
  box-shadow: 0 0 0 3px rgba(38, 149, 255, 0.1);
}

.payment-check {
  margin-bottom: 20px;
}

.payment-radio {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--color-nz);
  margin-bottom: 10px;
  margin-top: 10px;
}

.payment-content {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-dark);
}

.payment-icon {
  font-size: 24px;
  /* add spacing between the radio and the icon */
  margin-left: 8px;
  vertical-align: middle;
}

/* Place Order Button */
.btn-place-order {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, var(--color-nz), var(--color-nz2));
  color: var(--white);
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: var(--shadow-lg);
}

.btn-place-order:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
  background: linear-gradient(135deg, var(--color-nz2), var(--color-nz3));
}

.btn-place-order:active {
  transform: translateY(0);
}

.arrow {
  font-size: 20px;
  transition: transform 0.3s ease;
}

.btn-place-order:hover .arrow {
  transform: translateX(4px);
}

/* Animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 991px) {
  .modern-checkout {
    padding: 40px 0;
  }

  .checkout-card,
  .order-summary-card {
    padding: 24px;
  }

  .order-summary-card {
    position: static;
  }
}

@media (max-width: 767px) {
  .section-title {
    font-size: 18px;
  }

  .product-item {
    flex-wrap: wrap;
  }

  .product-thumb {
    width: 60px;
    height: 60px;
  }

  .product-price {
    width: 100%;
    text-align: right;
    font-size: 16px;
  }

  .total-price {
    font-size: 20px;
  }

  .checkout-card,
  .order-summary-card {
    padding: 20px;
  }
}

/* Utility classes for spacing */
.g-3>* {
  margin-bottom: 1rem;
}

@media (min-width: 768px) {
  .g-3 {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }

  .g-3>* {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

/* Billing Section */
.billing-section {
  margin-top: 32px;
}

/* Sticky behavior */
@media (min-width: 992px) {
  .sticky-top {
    position: sticky;
    top: 20px;
    z-index: 1020;
  }
}

/* Additional spacing utilities */
.mb-4 {
  margin-bottom: 1.5rem;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Focus visible for accessibility */
*:focus-visible {
  outline: 2px solid var(--color-nz);
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .modern-checkout {
    background: var(--white);
  }

  .checkout-card,
  .order-summary-card {
    box-shadow: none;
    border: 1px solid var(--gray-300);
  }

  .btn-place-order {
    display: none;
  }
}

/* Loading animation for spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spinner-border {
  animation: spin 0.75s linear infinite;
}

/* Additional hover effects */
.address-card:active {
  transform: translateY(0);
}

.payment-option:active {
  transform: translateX(0);
}

/* Select dropdown compatibility */
.v-select {
  background-color: var(--white);
}

.vs__search {
  border: none !important;
  width: auto !important;
}

/* Ensure proper spacing on all devices */
@media (max-width: 575px) {
  .modern-checkout {
    padding: 30px 0;
  }

  .section-title {
    font-size: 16px;
    margin-bottom: 16px;
  }

  .icon {
    font-size: 20px;
  }

  .checkout-card,
  .order-summary-card {
    padding: 16px;
    border-radius: 8px;
  }

  .form-control-modern {
    padding: 10px 14px;
    font-size: 14px;
  }

  .btn-place-order {
    padding: 14px;
    font-size: 15px;
  }

  .saved-addresses {
    margin-bottom: 24px;
    padding-bottom: 24px;
  }

  .billing-section {
    margin-top: 24px;
  }
}

/* Hover states for better interactivity */
.form-control-modern:hover:not(:focus) {
  border-color: var(--gray-400);
}

/* Disabled state */
.form-control-modern:disabled {
  background-color: var(--gray-100);
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-place-order:disabled {
  background: var(--gray-400);
  cursor: not-allowed;
  transform: none !important;
}

/* Success state animation */
@keyframes successPulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

/* Container max width for very large screens */
@media (min-width: 1400px) {
  .container {
    max-width: 1320px;
  }
}

/* Accessibility improvements */
.address-radio:focus,
.payment-radio:focus {
  outline: 2px solid var(--color-nz);
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {

  .address-card,
  .payment-option {
    border-width: 3px;
  }

  .form-control-modern {
    border-width: 3px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {

  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Force light mode always */
.modern-checkout {
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--white) 100%);
  color: var(--color-dark);
}

.checkout-card,
.order-summary-card {
  background: var(--white);
  border-color: var(--gray-200);
  color: var(--color-dark);
}

.section-title,
.form-group-modern label,
.address-content h6,
.product-info h6,
.payment-content {
  color: var(--color-dark);
}

.form-control-modern {
  background: var(--white);
  border-color: var(--gray-200);
  color: var(--color-dark);
}

.address-card,
.payment-option {
  background: var(--gray-50);
  border-color: var(--gray-200);
  color: var(--color-dark);
}

.address-content p,
.product-info .quantity,
.price-row {
  color: var(--gray-600);
}

/* Ensure light backgrounds for all interactive elements */
.address-card:hover,
.payment-option:hover,
.form-control-modern:focus {
  background: var(--white);
}

.address-card.selected,
.payment-option.selected {
  background: rgba(38, 149, 255, 0.05);
}

/* Light mode alert */
.alert-modern {
  background: #fee;
  border: 1px solid var(--color-oo4);
  color: var(--color-oo3);
}

/* Light mode loader */
.loader-overlay {
  background: rgba(255, 255, 255, 0.95);
}

/* Light login prompt */
.login-prompt {
  background: linear-gradient(135deg, rgba(38, 149, 255, 0.1), rgba(38, 149, 255, 0.05));
}
</style>